<template>
  <div>
    <el-drawer 
      id="map-selection-drawer"
      class="drawer"
      :withHeader="false" 
      :show-close="false" 
      :destroy-on-close="true" 
      :visible.sync="isVisible" 
      size="90%"
      direction="btt"
    >
    <!-- Header -->
      <el-row>
          <el-col class="text-center">
            <h2 class="text-title--large">
                {{ $t('drawer.locationSelection.title') }}
            </h2>
          </el-col>
      </el-row>

      <div class="content-container">
        <el-row class="gmap-container d-flex justify-center">
          <div class="search-container">
            <el-input
              tabindex="-1"
              v-model="search"
              class="text-subtitle search-input"
              :placeholder="$t('drawer.map.search')"
              @input="onInputSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"/>
              <i @click="clearSearch" slot="suffix" class="el-input__icon el-icon-error"/>
            </el-input>

            <div 
              v-if="predictedLocation.length"
              class="predicted-address-container mt--1 pa--2"
            >
              <el-row v-for="(item, index) in predictedLocation" :key="index">
                <div class="d-flex py--2" @click="onSelectLocation(item.description)">
                  <el-col :span="3" class="text-center pt--1">
                    <img :src="require('@/assets/icons/svg/pin-inactive.svg')" class="color-info--text" />
                  </el-col>
                  <el-col :span="24">
                    <el-col :span="24" class="text-xs-start text-subtitle--med">
                      {{ item.structured_formatting.main_text }}
                    </el-col>
                    <el-col :span="24" class="text-xs-start text-body2">{{ item.description }}</el-col>
                  </el-col>
                </div>
                <el-divider v-if="index < predictedLocation.length - 1" class="color-whitesmoke ma--0" />
              </el-row>
            </div>
          </div>

          <BaseButton 
            @click="useCurrentLocation" 
            :loading="currentLocationLoading"
            class="button-current-location"
          >
            <img :src="require('@/assets/icons/svg/gps-fixed.svg')" alt="gps-fixed" />
          </BaseButton>

          <Gmap
            :position="centerPosition"
            @change="onPinChangeLocation"
            :marker-icon="require('@/assets/icons/svg/pin.svg')"
            :marker-size="{ height: 60, width: 60 }"
            :zoom="18"
          />
        </el-row>
      </div>

      <div class="footer-container"> 
        <el-row class="pb--3 d-flex">
          <el-col :span="3" class="text-center pt--1">
            <img :src="require('@/assets/icons/svg/pin-active.svg')" />
          </el-col>
          <el-col :span="24">
            <el-col :span="24" class="text-heading3--med">{{ selectedLocation.address }}</el-col>
            <el-col :span="24" class="text-subtitle mt--2">{{ selectedLocation.address_longname }}</el-col>
          </el-col>
        </el-row>

        <el-row>
          <BaseButton 
            @click="submit" 
            class="color-primary color-white--text full-width"
            :loading="loading"
          >
            {{ $t('drawer.map.confirmaddress') }}
          </BaseButton>
        </el-row>
      </div>
    </el-drawer>

    <BaseDialog 
      class="warning-drawer"
      v-model="isCurrentLocationWarning"
      :withHeader="false" 
      :show-close="false" 
      :destroy-on-close="true"
      direction="btt"
    >
      <div class="text-xs-center" style="word-break: break-word;">
        <span class="text-heading3 color-black--text">
          {{ currentLocationWarning }}
        </span>
      </div>
    </BaseDialog>
  </div>
</template>

<script>

import BaseButton from '@/baseComponents/button/BaseButton.vue'
import BaseDialog from '@/baseComponents/dialog/BaseDialog.vue'

import Gmap from '@/components/element/Gmap'
import GeneralUtil from '@/utils/general'
import LocationUtil from '@/utils/location'

export default {
  name: "LocationSelectionDrawer",
  components: { 
    Gmap, 
    BaseButton,
    BaseDialog 
  },
  props: {
    value: { default: false },
    location: { default: () => ({}) }
  },
  data: function() {
    return {
      currentLocationLoading: false,
      isCurrentLocationWarning: false,
      currentLocationWarning: '',

      loading: false,
      centerPosition: {},
      selectedLocation: {},

      // Searching
      search: '',
      predictedLocation: [],
    }
  },
  computed: {
    isVisible: {
      get: function() {
        return this.value
      },
      set: function(value) {
        this.$emit('input', value)
      }
    },
  },
  watch: {
    isVisible: function(value) {
      value && this.initLocation()
    }
  },
  methods: {
    initLocation: function() {
      this.centerPosition = Object.assign({}, this.location)
      this.updateLocation(this.location)
    },
    submit: async function() {
      this.loading = true
      this.$emit('submit', { 
        location: this.selectedLocation, 
        done: () => { 
          this.isVisible = false
          this.loading = false
        }
      })
    },

    // Searching
    onInputSearch: function(value) {
			this.search = value
      this.updatePredictedLocation()
		},
    clearSearch: function() {
      this.search = ''
      this.updatePredictedLocation()
    },
    onSelectLocation: async function(address) {
      try {
        this.loading = true
        const [result] = await GeneralUtil.retry(() => LocationUtil.geocode({ address }))
        const location = this.formatGeocodingtoLocation(result)
        this.selectedLocation = location
        this.centerPosition = Object.assign({}, this.selectedLocation)
        this.search = address
        this.predictedLocation = []
      } finally {
        this.loading = false
      }
    },
    updatePredictedLocation: async function() {
      if (this.search) {
        const autoCompleteService = new google.maps.places.AutocompleteService()
        const { predictions = [] } = await autoCompleteService.getPlacePredictions({ input: this.search })
        return this.predictedLocation = predictions //.slice(0, 4)
      }
      this.predictedLocation = []
    },

    // Pining
    onPinChangeLocation: async function (location) {
      try {
        this.loading = true
        await this.updateLocation({ 
          lat: location.latLng.lat(),
          lng: location.latLng.lng(),
        })
      } finally {
        this.loading = false
      }
    },
    updateLocation: async function(location) {
      const [result] = await GeneralUtil.retry(() => LocationUtil.geocode({ location }))
      this.selectedLocation = this.formatGeocodingtoLocation(result)
    },

    // Using current location
    useCurrentLocation: async function() {
      try {
        this.currentLocationLoading = true
        const currentLocation = await LocationUtil.getCurrentLocation().then(this.formatGeocodingtoLocation)
        this.selectedLocation = currentLocation
        this.centerPosition = this.selectedLocation
      } catch (error) {
        if (error instanceof GeolocationPositionError) {
          this.isCurrentLocationWarning = true
          this.currentLocationWarning = error.code === 1 ? 
            this.$t('drawer.locationSetting.allowLocationWarning') : error.message
        }
        throw error
      } 
      finally {
        this.currentLocationLoading = false
      }
    },

    // Utils
    formatGeocodingtoLocation: function(geocodingItem) {
      return {
        lat: geocodingItem.geometry.location.lat(), 
        lng: geocodingItem.geometry.location.lng(),
        address: geocodingItem.formatted_address,
        address_longname: geocodingItem.address_components[0].long_name
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.warning-drawer {
  /deep/ {
    .el-dialog {
      width: 280px;
    }
    .el-dialog__header {
      padding: 0;
    }
  }
}

#map-selection-drawer {
  /deep/ {
    .el-drawer__body {
      display: flex;
      flex-direction: column;
      overflow: unset !important;
    }
  }

  .content-container {
    height: 100%;
  }

  .footer-container {
    margin-top: auto;
    padding: 16px;
    position: sticky;
    bottom: 0;
    background: var(--color-white);
  }

  .gmap-container {
    height: 100%;
    position: relative;
  }

  .search-container {
    position: absolute;
    text-align: center;
    top: 20px;
    z-index: 2;
    width: 100%;
  }

  .predicted-address-container {
    display: inline-block;
    width: 90%;
    background: #FFFFFF;
    border: 1px solid #E0E0E0;
    box-sizing: border-box;
    border-radius: 12px;
  }

  .search-input {
    width: 90%;

    /deep/ {
      input {
        border-radius: 19px;
      }
    }
  }

  .button-current-location {
    display: flex;
    justify-content: center;
    position: absolute;
    z-index: 1;
    bottom: 10px;
    right: 10px;
    border-radius: 50px;
    width: 44px;
    height: 44px;
    background: linear-gradient(360deg, #FFFFFF 0%, #FFFFFF 100%);
    box-shadow: 0px 1px 2px rgba(208, 208, 208, 0.5), 0px 8px 12px rgba(41, 51, 64, 0.2);
  }
}
</style>


// WEBPACK FOOTER //
// src/components/locationselection/LocationSelectionDrawer.vue